import type { ClientProject } from './api';

export type ClientGenerationTask = {
  id: string;
  projectId?: string;
  sceneIndex?: number;
  type: 'story_create' | 'story_continue' | 'scene_rewrite' | 'scene_image' | 'scene_audio';
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  progress: number;
  currentStep: string;
  attempts: number;
  retryable: boolean;
  errorCode?: string;
  errorMessage?: string;
  suggestion?: string;
  resultRef?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  finishedAt?: string;
};

export type GenerationTaskProgress = Pick<ClientGenerationTask, 'status' | 'progress' | 'currentStep' | 'attempts'>;

export type GenerationTaskPollerOptions = {
  signal?: AbortSignal;
  intervalMs?: number;
  maxIntervalMs?: number;
  maxWaitMs?: number;
  onProgress?: (task: ClientGenerationTask) => void;
};

export const DEFAULT_GENERATION_TASK_MAX_WAIT_MS = 15 * 60 * 1000;

function abortError() {
  return new DOMException('请求已取消', 'AbortError');
}

function wait(ms: number, signal?: AbortSignal) {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise<void>((resolve, reject) => {
    const timer = globalThis.setTimeout(resolve, ms);
    const abort = () => {
      globalThis.clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export class GenerationTaskPoller {
  constructor(
    private readonly read: (taskId: string, signal?: AbortSignal) => Promise<ClientGenerationTask>,
    private readonly defaults: GenerationTaskPollerOptions = {},
  ) {}

  poll(taskId: string, options: GenerationTaskPollerOptions = {}) {
    return pollGenerationTask(taskId, this.read, { ...this.defaults, ...options });
  }
}

export async function pollGenerationTask(
  taskId: string,
  read: (taskId: string, signal?: AbortSignal) => Promise<ClientGenerationTask>,
  options: GenerationTaskPollerOptions = {},
): Promise<ClientGenerationTask> {
  let delay = options.intervalMs ?? 800;
  const maxDelay = options.maxIntervalMs ?? 4_000;
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_GENERATION_TASK_MAX_WAIT_MS;
  const startedAt = Date.now();
  const timeoutError = () => Object.assign(new Error('生成任务等待超时，请刷新页面查看最新结果。'), {
    code: 'GENERATION_TASK_TIMEOUT',
    retryable: true,
  });
  while (true) {
    if (Date.now() - startedAt >= maxWaitMs) throw timeoutError();
    const task = await read(taskId, options.signal);
    options.onProgress?.(task);
    if (task.status === 'succeeded' || task.status === 'failed' || task.status === 'cancelled') {
      if (task.status !== 'succeeded') {
        const error = new Error(task.errorMessage || '生成任务失败');
        error.name = task.errorCode || 'GENERATION_TASK_FAILED';
        throw Object.assign(error, {
          code: task.errorCode,
          suggestion: task.suggestion,
          retryable: task.retryable,
        });
      }
      return task;
    }
    const remaining = maxWaitMs - (Date.now() - startedAt);
    if (remaining <= 0) throw timeoutError();
    await wait(Math.min(delay, remaining), options.signal);
    delay = Math.min(maxDelay, Math.round(delay * 1.35));
  }
}

export async function resolveGenerationProject(
  response: { project?: ClientProject; taskId?: string },
  readTask: (taskId: string, signal?: AbortSignal) => Promise<ClientGenerationTask>,
  readProject: (projectId: string, signal?: AbortSignal) => Promise<ClientProject>,
  options: GenerationTaskPollerOptions = {},
) {
  if (response.project && !response.taskId) return response.project;
  if (!response.taskId) throw new Error('GENERATION_TASK_ID_MISSING');
  const task = await pollGenerationTask(response.taskId, readTask, options);
  const projectId = task.projectId || response.project?.id;
  if (!projectId) throw new Error('GENERATION_PROJECT_ID_MISSING');
  return readProject(projectId, options.signal);
}
